import {
  collection, doc, addDoc, setDoc, getDoc, getDocs,
  updateDoc, deleteDoc, query, where, orderBy, serverTimestamp,
  onSnapshot, writeBatch,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, secondaryAuth, storage } from "./config";
import { COLLECTIONS, DAY_TYPES } from "./schema";
import { mapFields, HIGHEND_GPU, specDocId } from "../utils/masterSpecs";

// ── AUTH ────────────────────────────────────────────────────
// Actor hiện tại (đặt khi login / khi useAuth tải profile) — để auto điền vào audit log
let currentActor = null;
export const setAuditActor = (a) => { currentActor = a || null; };

export const loginUser = async (email, password) => {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  try {
    const p = await getUserProfile(cred.user.uid);
    setAuditActor(p ? { name: p.name, role: p.role, uid: p.uid } : null);
    await addAuditLog({
      type: "login", actorName: p?.name || email, actorRole: p?.role || "", actorId: cred.user.uid,
      detail: `${p?.name || email} đăng nhập`,
    });
  } catch { /* không chặn login nếu ghi log lỗi */ }
  return cred;
};

export const logoutUser = async () => {
  try {
    await addAuditLog({ type: "logout", detail: `${currentActor?.name || ""} đăng xuất` });
  } catch { /* ignore */ }
  setAuditActor(null);
  return signOut(auth);
};

// Tạo tài khoản Auth trên app phụ (admin vẫn đăng nhập trên `auth`), rồi ghi users/{uid}
// bằng phiên admin. Ghi profile lỗi → xoá tài khoản Auth vừa tạo để không bị mồ côi.
const createAccount = async (email, password, profile) => {
  const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  try {
    await setDoc(doc(db, COLLECTIONS.USERS, cred.user.uid), { ...profile, email, createdAt: serverTimestamp() });
    return cred.user;
  } catch (err) {
    await deleteUser(cred.user).catch(() => {});
    throw err;
  } finally {
    await signOut(secondaryAuth).catch(() => {});
  }
};

export const createPromoter = async ({ email, password, name, shopName, baseSalary, allowance, startDate, dependents }) => {
  const user = await createAccount(email, password, {
    role: "promoter", name, shopName,
    baseSalary: Number(baseSalary), allowance: Number(allowance) || 0,
    startDate: startDate || null, dependents: Number(dependents) || 0,
  });
  addAuditLog({ type: "promoter_created", promoterName: name, detail: `Tạo promoter "${name}" (${shopName || ""})`, meta: {} });
  return user;
};

export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, COLLECTIONS.USERS, uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
};

export const getAllPromoters = async (includeArchived = false) => {
  const q = query(collection(db, COLLECTIONS.USERS), where("role", "==", "promoter"));
  const snap = await getDocs(q);
  const all = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
  return includeArchived ? all : all.filter(p => p.active !== false);
};

export const updatePromoter = (uid, data) => {
  addAuditLog({ type: "promoter_updated", promoterName: data?.name || "", detail: `Sửa promoter "${data?.name || uid}"`, meta: { uid } });
  return updateDoc(doc(db, COLLECTIONS.USERS, uid), data);
};

export const archivePromoter = (uid) => {
  addAuditLog({ type: "promoter_archived", detail: `Lưu trữ promoter`, meta: { uid } });
  return updateDoc(doc(db, COLLECTIONS.USERS, uid), { active: false });
};

export const restorePromoter = (uid) => {
  addAuditLog({ type: "promoter_restored", detail: `Khôi phục promoter`, meta: { uid } });
  return updateDoc(doc(db, COLLECTIONS.USERS, uid), { active: true });
};

// Gửi email đặt lại mật khẩu (dùng cho quên mật khẩu và admin gửi hộ promoter)
export const sendPasswordReset = (email) =>
  sendPasswordResetEmail(auth, email);

// Đổi mật khẩu người dùng hiện tại (cần re-authenticate trước)
export const changeCurrentPassword = async (currentPassword, newPassword) => {
  const user = auth.currentUser;
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
  addAuditLog({ type: "password_changed", detail: `Đổi mật khẩu` });
};

// ── AUDIT LOG ──────────────────────────────────────────────
// Tự điền actor (tên/vai trò/uid) từ currentActor nếu lời gọi không truyền vào.
export const addAuditLog = (data = {}) =>
  addDoc(collection(db, COLLECTIONS.AUDIT_LOGS), {
    actorName: currentActor?.name || "",
    actorRole: currentActor?.role || "",
    actorId: currentActor?.uid || "",
    ...data,
    createdAt: serverTimestamp(),
  });

export const getAuditLogs = async ({ type, month } = {}) => {
  let q = query(collection(db, COLLECTIONS.AUDIT_LOGS), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  let logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (type) logs = logs.filter(l => l.type === type);
  if (month) logs = logs.filter(l => l.meta?.month === month || l.createdAt?.toDate?.()?.toISOString?.().slice(0, 7) === month);
  return logs;
};

// ── TARGETS ────────────────────────────────────────────────
// Admin đặt → approved (hiệu lực ngay). Supervisor đặt → pending, chờ admin duyệt.
// Supervisor KHÔNG sửa được target đã approved.
export const setTarget = async ({ promoterId, month, sellTarget, highendTarget, actorName = "Admin", actorRole = "admin", actorId = "", promoterName = "" }) => {
  const q = query(
    collection(db, COLLECTIONS.TARGETS),
    where("promoterId", "==", promoterId),
    where("month", "==", month)
  );
  const snap = await getDocs(q);
  const existing = snap.empty ? null : snap.docs[0].data();

  const isSupervisor = actorRole === "supervisor";
  // Target cũ không có status → coi như đã duyệt (tương thích ngược)
  if (isSupervisor && existing && existing.status !== "pending") {
    throw new Error("Target đã được duyệt — liên hệ admin để thay đổi");
  }

  const data = {
    promoterId, month, promoterName,
    sellTarget: Number(sellTarget), highendTarget: Number(highendTarget),
    status: isSupervisor ? "pending" : "approved",
    ...(isSupervisor ? {
      proposedSell: Number(sellTarget), proposedHighend: Number(highendTarget),
      proposedBy: actorId, proposedByName: actorName,
    } : { approvedBy: actorName, approvedAt: serverTimestamp() }),
  };
  if (!snap.empty) {
    await updateDoc(snap.docs[0].ref, data);
  } else {
    await addDoc(collection(db, COLLECTIONS.TARGETS), { ...data, createdAt: serverTimestamp() });
  }
  await addAuditLog({
    type: "target_set", actorName, actorRole, promoterId, promoterName,
    detail: `${actorName} ${isSupervisor ? "đề xuất" : "đặt"} target tháng ${month}: DS=${sellTarget}, HE=${highendTarget}${isSupervisor ? " (chờ duyệt)" : ""}`,
    meta: { month, sellTarget: Number(sellTarget), highendTarget: Number(highendTarget) },
  });
};

// Danh sách target chờ duyệt (mọi tháng)
export const getPendingTargets = async () => {
  const q = query(collection(db, COLLECTIONS.TARGETS), where("status", "==", "pending"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.month || "").localeCompare(a.month || ""));
};

// Admin duyệt target (có thể đã chỉnh số) → báo supervisor + promoter
export const approveTarget = async (target, { sellTarget, highendTarget }, adminName = "Admin") => {
  const finalSell = Number(sellTarget);
  const finalHE = Number(highendTarget);
  await updateDoc(doc(db, COLLECTIONS.TARGETS, target.id), {
    sellTarget: finalSell, highendTarget: finalHE,
    status: "approved", approvedBy: adminName, approvedAt: serverTimestamp(),
  });

  const changed = finalSell !== target.proposedSell || finalHE !== target.proposedHighend;
  const changeText = changed
    ? ` (điều chỉnh từ đề xuất DS ${target.proposedSell} → ${finalSell}, HE ${target.proposedHighend} → ${finalHE})`
    : "";

  // Báo supervisor đề xuất
  if (target.proposedBy) {
    await sendNotification({
      title: `✅ Target tháng ${target.month} đã được duyệt`,
      body: `Target của ${target.promoterName || "promoter"} tháng ${target.month}: DS ${finalSell} máy, HE ${finalHE} máy${changeText}. Duyệt bởi ${adminName}.`,
      fileUrl: null,
      sentTo: [target.proposedBy],
    });
  }
  // Báo promoter — target chính thức có hiệu lực
  await sendNotification({
    title: `🎯 Target tháng ${target.month} của bạn`,
    body: `Target tháng ${target.month}: ${finalSell} máy (Doanh số), ${finalHE} máy (Highend).`,
    fileUrl: null,
    sentTo: [target.promoterId],
  });

  await addAuditLog({
    type: "target_approved", actorName: adminName, actorRole: "admin",
    promoterId: target.promoterId, promoterName: target.promoterName || "",
    detail: `${adminName} duyệt target tháng ${target.month} của ${target.promoterName || ""}: DS=${finalSell}, HE=${finalHE}${changeText}`,
    meta: { month: target.month, sellTarget: finalSell, highendTarget: finalHE },
  });
};

// Mặc định CHỈ trả target đã duyệt (promoter/tính lương). includePending=true cho supervisor/admin xem đề xuất.
export const getTarget = async (promoterId, month, includePending = false) => {
  const q = query(
    collection(db, COLLECTIONS.TARGETS),
    where("promoterId", "==", promoterId),
    where("month", "==", month)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const t = { id: snap.docs[0].id, ...snap.docs[0].data() };
  if (!includePending && t.status === "pending") return null;
  return t;
};

export const getAllTargets = async (month, approvedOnly = true) => {
  const q = query(collection(db, COLLECTIONS.TARGETS), where("month", "==", month));
  const snap = await getDocs(q);
  let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (approvedOnly) list = list.filter(t => t.status !== "pending");
  return list;
};

// ── SALES ──────────────────────────────────────────────────
export const submitSale = async ({ promoterId, promoterName, model, serial, isHighend, saleDate, month }) => {
  const res = await addDoc(collection(db, COLLECTIONS.SALES), {
    promoterId, promoterName, model, serial, isHighend,
    saleDate, month, status: "pending",
    submittedAt: serverTimestamp(), approvedAt: null,
  });
  addAuditLog({ type: "sale_submitted", promoterId, promoterName, detail: `${promoterName} nhập đơn ${model} (${serial})`, meta: { month, model, serial } });
  return res;
};

export const approveSale = async (saleId, approverName = "Admin", approverRole = "admin") => {
  // Lấy thông tin sale trước để ghi log
  const snap = await getDoc(doc(db, COLLECTIONS.SALES, saleId));
  const sale = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.SALES, saleId), {
    status: "approved", approvedAt: serverTimestamp(),
    approvedBy: approverName, approverRole,
  });
  await addAuditLog({
    type: "sale_approved", actorName: approverName, actorRole: approverRole,
    promoterId: sale.promoterId || "", promoterName: sale.promoterName || "",
    detail: `${approverName} duyệt sale ${sale.model || ""} (${sale.serial || ""}) của ${sale.promoterName || ""}`,
    meta: { saleId, model: sale.model, serial: sale.serial, month: sale.month },
  });
};

export const rejectSale = async (saleId, approverName = "Admin", approverRole = "admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.SALES, saleId));
  const sale = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.SALES, saleId), { status: "rejected" });
  await addAuditLog({
    type: "sale_rejected", actorName: approverName, actorRole: approverRole,
    promoterId: sale.promoterId || "", promoterName: sale.promoterName || "",
    detail: `${approverName} từ chối sale ${sale.model || ""} (${sale.serial || ""}) của ${sale.promoterName || ""}`,
    meta: { saleId, model: sale.model, serial: sale.serial, month: sale.month },
  });
};

// ── HỦY ĐƠN ĐÃ DUYỆT (promoter yêu cầu → QL duyệt) ─────────
export const requestCancelSale = (saleId, reason) =>
  updateDoc(doc(db, COLLECTIONS.SALES, saleId),
    { cancelRequested: true, cancelReason: reason, cancelRequestedAt: serverTimestamp() });

export const approveCancelSale = async (saleId, byName = "Admin", byRole = "admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.SALES, saleId));
  const s = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.SALES, saleId),
    { status: "cancelled", cancelRequested: false, cancelledBy: byName, cancelledAt: serverTimestamp() });
  await addAuditLog({
    type: "sale_cancelled", actorName: byName, actorRole: byRole,
    promoterId: s.promoterId || "", promoterName: s.promoterName || "",
    detail: `${byName} duyệt hủy đơn ${s.model || ""} (${s.serial || ""}) của ${s.promoterName || ""} — lý do: ${s.cancelReason || ""}`,
    meta: { saleId, model: s.model, serial: s.serial, month: s.month },
  });
};

export const rejectCancelSale = async (saleId, byName = "Admin", byRole = "admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.SALES, saleId));
  const s = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.SALES, saleId), { cancelRequested: false });
  await addAuditLog({
    type: "sale_cancel_rejected", actorName: byName, actorRole: byRole,
    promoterId: s.promoterId || "", promoterName: s.promoterName || "",
    detail: `${byName} từ chối yêu cầu hủy đơn ${s.model || ""} (${s.serial || ""}) của ${s.promoterName || ""}`,
    meta: { saleId, model: s.model, serial: s.serial, month: s.month },
  });
};

// Tìm mọi đơn (mọi trạng thái) theo danh sách serial — dùng cảnh báo serial trùng ở tab chờ duyệt
// promoterIds: supervisor chỉ được đọc đơn trong nhóm mình → chỉ dò trùng trong nhóm
export const getSalesBySerials = async (serials, promoterIds = null) => {
  const uniq = [...new Set((serials || []).filter(Boolean))];
  if (uniq.length === 0) return {};
  const lists = await Promise.all(uniq.map(sn => promoterIds
    ? queryByPromoters(COLLECTIONS.SALES, promoterIds, where("serial", "==", sn))
    : getDocs(query(collection(db, COLLECTIONS.SALES), where("serial", "==", sn)))
        .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })))
  ));
  const map = {};
  lists.forEach((list, i) => { map[uniq[i]] = list; });
  return map; // { serial: [đơn...] }
};

// Thu hồi duyệt (duyệt nhầm) → đơn quay về Chờ duyệt — admin + supervisor
export const revertSaleApproval = async (saleId, byName = "Admin", byRole = "admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.SALES, saleId));
  const s = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.SALES, saleId),
    { status: "pending", approvedAt: null, approvedBy: null, approverRole: null });
  await addAuditLog({
    type: "sale_approval_reverted", actorName: byName, actorRole: byRole,
    promoterId: s.promoterId || "", promoterName: s.promoterName || "",
    detail: `${byName} thu hồi duyệt đơn ${s.model || ""} (${s.serial || ""}) của ${s.promoterName || ""}`,
    meta: { saleId, model: s.model, serial: s.serial, month: s.month },
  });
};

// Sửa cờ Highend của MỘT đơn (ảnh hưởng thưởng HE) — admin + supervisor
export const updateSaleHighend = async (saleId, isHighend, byName = "Admin", byRole = "admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.SALES, saleId));
  const s = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.SALES, saleId), { isHighend: !!isHighend });
  await addAuditLog({
    type: "sale_highend_changed", actorName: byName, actorRole: byRole,
    promoterId: s.promoterId || "", promoterName: s.promoterName || "",
    detail: `${byName} đổi Highend của ${s.model || ""} (${s.serial || ""}) → ${isHighend ? "Có" : "Không"}`,
    meta: { saleId, model: s.model, serial: s.serial, month: s.month },
  });
};

// Danh sách yêu cầu hủy (approved + cancelRequested)
export const getCancelRequests = async (promoterIds = null) => {
  const filters = [where("status", "==", "approved"), where("cancelRequested", "==", true)];
  if (promoterIds) return queryByPromoters(COLLECTIONS.SALES, promoterIds, ...filters);
  const snap = await getDocs(query(collection(db, COLLECTIONS.SALES), ...filters));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getSalesByMonth = async (month, promoterId = null) => {
  let q = query(
    collection(db, COLLECTIONS.SALES),
    where("month", "==", month),
    where("status", "==", "approved")
  );
  if (promoterId) q = query(q, where("promoterId", "==", promoterId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getPendingSales = async () => {
  const q = query(collection(db, COLLECTIONS.SALES), where("status", "==", "pending"), orderBy("submittedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getPromoterPendingSales = async (promoterId, month) => {
  const q = query(
    collection(db, COLLECTIONS.SALES),
    where("promoterId", "==", promoterId),
    where("month", "==", month),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Lấy TẤT CẢ đơn của promoter trong tháng (mọi trạng thái: pending/approved/rejected)
export const getPromoterSalesByMonth = async (promoterId, month) => {
  const q = query(
    collection(db, COLLECTIONS.SALES),
    where("promoterId", "==", promoterId),
    where("month", "==", month)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const subscribePendingSales = (callback) => {
  const q = query(collection(db, COLLECTIONS.SALES), where("status", "==", "pending"), orderBy("submittedAt", "desc"));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
};

// ── ATTENDANCE ─────────────────────────────────────────────
// Nén ảnh thường trả JPEG, nhưng khi nén thất bại ta upload file gốc (có thể là HEIC/PNG).
// Đặt đúng đuôi + contentType để ảnh mở được ở trang admin, thay vì luôn gắn cứng .jpg.
export const uploadAttendancePhoto = async (uid, type, file) => {
  const mime = file?.type || "image/jpeg";
  const ext = mime.includes("png") ? "png" : /hei[cf]/.test(mime) ? "heic" : "jpg";
  const path = `attendance/${uid}/${type}_${Date.now()}.${ext}`;
  const r = ref(storage, path);
  await uploadBytes(r, file, { contentType: mime });
  return getDownloadURL(r);
};

export const checkIn = async ({ promoterId, date, photoUrl, lat, lng, isHoliday, holidayMultiplier, scheduledHours = 8, dayCredit }) => {
  const existing = await getAttendanceByDate(promoterId, date);
  if (existing) throw new Error("Đã check-in hôm nay rồi");
  return addDoc(collection(db, COLLECTIONS.ATTENDANCE), {
    promoterId, date,
    checkinTime: new Date().toTimeString().slice(0, 5),
    checkinPhoto: photoUrl, checkinLat: lat, checkinLng: lng,
    checkoutTime: null, checkoutPhoto: null,
    checkoutLat: null, checkoutLng: null,
    totalHours: null, isHoliday: !!isHoliday,
    holidayMultiplier: holidayMultiplier || 1,
    scheduledHours, dayCredit: dayCredit ?? scheduledHours / 8,
  });
};

export const checkOut = async ({ attendanceId, photoUrl, lat, lng }) => {
  const snap = await getDoc(doc(db, COLLECTIONS.ATTENDANCE, attendanceId));
  const data = snap.data();
  const now = new Date().toTimeString().slice(0, 5);
  const [h1, m1] = data.checkinTime.split(":").map(Number);
  const [h2, m2] = now.split(":").map(Number);
  const totalHours = ((h2 * 60 + m2) - (h1 * 60 + m1)) / 60;
  return updateDoc(doc(db, COLLECTIONS.ATTENDANCE, attendanceId), {
    checkoutTime: now, checkoutPhoto: photoUrl,
    checkoutLat: lat, checkoutLng: lng,
    totalHours: Math.round(totalHours * 100) / 100,
  });
};

export const getAttendanceByDate = async (promoterId, date) => {
  const q = query(
    collection(db, COLLECTIONS.ATTENDANCE),
    where("promoterId", "==", promoterId),
    where("date", "==", date)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const getAttendanceByMonth = async (promoterId, monthPrefix) => {
  const q = query(
    collection(db, COLLECTIONS.ATTENDANCE),
    where("promoterId", "==", promoterId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => r.date && r.date.startsWith(monthPrefix))
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const getAllAttendanceByMonth = async (monthPrefix) => {
  const snap = await getDocs(collection(db, COLLECTIONS.ATTENDANCE));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => r.date && r.date.startsWith(monthPrefix));
};

// ── ATTENDANCE APPEALS ─────────────────────────────────────
export const submitAttendanceAppeal = (data) =>
  addDoc(collection(db, COLLECTIONS.ATTENDANCE_APPEALS), {
    ...data, status: "pending", submittedAt: serverTimestamp(),
    reviewedAt: null, adminNote: null,
  });

export const getAppealsByPromoter = async (promoterId) => {
  const q = query(
    collection(db, COLLECTIONS.ATTENDANCE_APPEALS),
    where("promoterId", "==", promoterId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAppealByDate = async (promoterId, date) => {
  const q = query(
    collection(db, COLLECTIONS.ATTENDANCE_APPEALS),
    where("promoterId", "==", promoterId),
    where("date", "==", date)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const getPendingAppeals = async () => {
  const q = query(
    collection(db, COLLECTIONS.ATTENDANCE_APPEALS),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0));
};

// Toàn bộ giải trình (mọi trạng thái) — dùng cho view Lịch sử; lọc theo tháng nếu có
export const getAllAppeals = async (monthPrefix = null) => {
  const snap = await getDocs(collection(db, COLLECTIONS.ATTENDANCE_APPEALS));
  let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (monthPrefix) list = list.filter(a => (a.date || "").startsWith(monthPrefix));
  return list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
};

export const approveAppeal = async (appeal) => {
  await updateDoc(doc(db, COLLECTIONS.ATTENDANCE_APPEALS, appeal.id), {
    status: "approved", reviewedAt: serverTimestamp(),
  });
  if (appeal.attendanceId) {
    await updateDoc(doc(db, COLLECTIONS.ATTENDANCE, appeal.attendanceId), {
      checkoutTime: "17:00", totalHours: 8, appealApproved: true,
    });
  } else {
    await addDoc(collection(db, COLLECTIONS.ATTENDANCE), {
      promoterId: appeal.promoterId, date: appeal.date,
      checkinTime: "08:00", checkinPhoto: null, checkinLat: 0, checkinLng: 0,
      checkoutTime: "17:00", checkoutPhoto: null, checkoutLat: 0, checkoutLng: 0,
      totalHours: 8, isHoliday: false, holidayMultiplier: 1, appealApproved: true,
    });
  }
};

export const rejectAppeal = (appealId, adminNote = "") =>
  updateDoc(doc(db, COLLECTIONS.ATTENDANCE_APPEALS, appealId), {
    status: "rejected", reviewedAt: serverTimestamp(), adminNote,
  });

// ── HOLIDAYS ───────────────────────────────────────────────
export const addHoliday = (data) => {
  addAuditLog({ type: "holiday_added", detail: `Thêm ngày lễ ${data?.date || ""}${data?.name ? ` (${data.name})` : ""}`, meta: { date: data?.date } });
  return addDoc(collection(db, COLLECTIONS.HOLIDAYS), data);
};

export const deleteHoliday = (id) => {
  addAuditLog({ type: "holiday_deleted", detail: `Xóa ngày lễ`, meta: { id } });
  return deleteDoc(doc(db, COLLECTIONS.HOLIDAYS, id));
};

export const getHolidays = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.HOLIDAYS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const isHolidayDate = async (dateStr) => {
  const holidays = await getHolidays();
  return holidays.find(h => h.date === dateStr) || null;
};

// ── BONUS CONFIG ───────────────────────────────────────────
export const getBonusConfig = async () => {
  const snap = await getDoc(doc(db, COLLECTIONS.BONUS_CONFIG, "main"));
  return snap.exists() ? snap.data() : null;
};

export const saveBonusConfig = (data) => {
  addAuditLog({ type: "bonus_config_saved", detail: `Cập nhật quy tắc thưởng`, meta: {} });
  return setDoc(doc(db, COLLECTIONS.BONUS_CONFIG, "main"), { ...data, updatedAt: serverTimestamp() });
};

// ── SKU ────────────────────────────────────────────────────
export const addSKU = (data) => {
  addAuditLog({ type: "sku_added", detail: `Thêm SKU: ${data?.model || ""}`, meta: { model: data?.model } });
  return addDoc(collection(db, COLLECTIONS.SKUS), { ...data, createdAt: serverTimestamp() });
};

export const getAllSKUs = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.SKUS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteSKU = (id) => {
  addAuditLog({ type: "sku_deleted", detail: `Xóa SKU`, meta: { id } });
  return deleteDoc(doc(db, COLLECTIONS.SKUS, id));
};

// ── BONUS PROGRAMS ─────────────────────────────────────────
// Tạo / sửa / thu hồi chương trình thưởng — mỗi lần đều báo ngay cho toàn bộ promoter + supervisor (sentTo "all").
const progDmy = (s) => s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "";
const progMoney = (v) => `${Number(v || 0).toLocaleString("vi-VN")}đ`;
const progSkuText = (skus = []) => skus.slice(0, 8).join(", ") + (skus.length > 8 ? ` và ${skus.length - 8} model khác` : "");
const progSummary = (p) => {
  const skus = Array.isArray(p.skus) ? p.skus : [];
  return `Thời gian: ${progDmy(p.startDate)} → ${progDmy(p.endDate)}\n`
    + `Thưởng: ${progMoney(p.amountPerUnit)} / máy\n`
    + `Áp dụng cho ${skus.length} model: ${progSkuText(skus)}`;
};
const notifyProgram = async ({ title, body, programId, auditDetail }) => {
  try {
    await sendNotification({ title, body, fileUrl: null, sentTo: "all", type: "bonus_program", programId });
    addAuditLog({ type: "notification_sent", detail: auditDetail, meta: { programId } });
  } catch (e) {
    console.error("Không gửi được thông báo chương trình thưởng", e);
  }
};

export const addBonusProgram = async (data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.BONUS_PROGRAMS), { ...data, status: "active", createdAt: serverTimestamp() });
  await notifyProgram({
    title: `🎁 Chương trình thưởng mới: ${data.name}`,
    body: progSummary(data),
    programId: ref.id,
    auditDetail: `Tự động báo chương trình thưởng "${data.name}" cho toàn bộ promoter & supervisor`,
  });
  return ref;
};

// Sửa chương trình — thông báo nêu rõ những gì thay đổi
export const updateBonusProgram = async (id, data, before = {}, byName = "Admin") => {
  await updateDoc(doc(db, COLLECTIONS.BONUS_PROGRAMS, id), { ...data, updatedAt: serverTimestamp(), updatedBy: byName });
  const changes = [];
  if ((before.name || "") !== (data.name || "")) changes.push(`• Tên: "${before.name || ""}" → "${data.name}"`);
  if (before.startDate !== data.startDate || before.endDate !== data.endDate) {
    changes.push(`• Thời gian: ${progDmy(before.startDate)} → ${progDmy(before.endDate)}  ⇒  ${progDmy(data.startDate)} → ${progDmy(data.endDate)}`);
  }
  if (Number(before.amountPerUnit) !== Number(data.amountPerUnit)) {
    changes.push(`• Thưởng mỗi máy: ${progMoney(before.amountPerUnit)} → ${progMoney(data.amountPerUnit)}`);
  }
  const oldSkus = Array.isArray(before.skus) ? before.skus : [];
  const newSkus = Array.isArray(data.skus) ? data.skus : [];
  const added = newSkus.filter(m => !oldSkus.includes(m));
  const removed = oldSkus.filter(m => !newSkus.includes(m));
  if (added.length) changes.push(`• Thêm model: ${progSkuText(added)}`);
  if (removed.length) changes.push(`• Bỏ model: ${progSkuText(removed)}`);
  addAuditLog({
    type: "bonus_program_updated",
    detail: `${byName} sửa chương trình thưởng "${data.name}"${changes.length ? ": " + changes.map(c => c.replace("• ", "")).join("; ") : ""}`,
    meta: { programId: id },
  });
  await notifyProgram({
    title: `✏️ Chương trình thưởng được cập nhật: ${data.name}`,
    body: (changes.length ? `Thay đổi:\n${changes.join("\n")}\n\n` : "") + `Thông tin mới:\n${progSummary(data)}`,
    programId: id,
    auditDetail: `Tự động báo cập nhật chương trình thưởng "${data.name}" cho toàn bộ promoter & supervisor`,
  });
};

// Thu hồi: chương trình không còn tính thưởng cho bất kỳ đơn nào (giữ lại để xem lịch sử, có thể khôi phục).
// Muốn dừng sớm mà vẫn giữ thưởng các đơn đã bán thì sửa ngày kết thúc thay vì thu hồi.
export const revokeBonusProgram = async (prog, reason = "", byName = "Admin") => {
  await updateDoc(doc(db, COLLECTIONS.BONUS_PROGRAMS, prog.id), {
    status: "revoked", revokedAt: serverTimestamp(), revokedBy: byName, revokeReason: reason || "",
  });
  addAuditLog({
    type: "bonus_program_revoked",
    detail: `${byName} thu hồi chương trình thưởng "${prog.name}"${reason ? ` — ${reason}` : ""}`,
    meta: { programId: prog.id },
  });
  await notifyProgram({
    title: `⛔ Chương trình thưởng đã bị thu hồi: ${prog.name}`,
    body: `Chương trình "${prog.name}" (${progDmy(prog.startDate)} → ${progDmy(prog.endDate)}) đã bị thu hồi và không còn được tính thưởng.`
      + (reason ? `\nLý do: ${reason}` : ""),
    programId: prog.id,
    auditDetail: `Tự động báo thu hồi chương trình thưởng "${prog.name}" cho toàn bộ promoter & supervisor`,
  });
};

export const restoreBonusProgram = async (prog, byName = "Admin") => {
  await updateDoc(doc(db, COLLECTIONS.BONUS_PROGRAMS, prog.id), {
    status: "active", restoredAt: serverTimestamp(), restoredBy: byName,
  });
  addAuditLog({ type: "bonus_program_restored", detail: `${byName} khôi phục chương trình thưởng "${prog.name}"`, meta: { programId: prog.id } });
  await notifyProgram({
    title: `♻️ Chương trình thưởng được áp dụng lại: ${prog.name}`,
    body: progSummary(prog),
    programId: prog.id,
    auditDetail: `Tự động báo khôi phục chương trình thưởng "${prog.name}" cho toàn bộ promoter & supervisor`,
  });
};

export const getActiveBonusPrograms = async (dateStr) => {
  const snap = await getDocs(collection(db, COLLECTIONS.BONUS_PROGRAMS));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(p => p.startDate <= dateStr && p.endDate >= dateStr);
};

export const getAllBonusPrograms = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.BONUS_PROGRAMS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── EVENT BONUSES (khoán sự kiện) ──────────────────────────
export const addEventBonus = (data) => {
  addAuditLog({ type: "event_bonus_added", promoterId: data?.promoterId, detail: `Khoán sự kiện "${data?.name || ""}" ${Number(data?.amount || 0).toLocaleString("vi-VN")}đ`, meta: { month: data?.month } });
  return addDoc(collection(db, COLLECTIONS.EVENT_BONUSES), { ...data, amount: Number(data.amount), createdAt: serverTimestamp() });
};

export const getEventBonuses = async (month, promoterIds = null) => {
  const snap = await getDocs(collection(db, COLLECTIONS.EVENT_BONUSES));
  let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (month) list = list.filter(b => b.month === month);
  if (promoterIds) list = list.filter(b => promoterIds.includes(b.promoterId));
  return list;
};

export const deleteEventBonus = (id) => {
  addAuditLog({ type: "event_bonus_deleted", detail: `Xóa khoán sự kiện`, meta: { id } });
  return deleteDoc(doc(db, COLLECTIONS.EVENT_BONUSES, id));
};

// ── INCOME HISTORY (import từ Excel, tháng cũ) ─────────────
export const setIncomeHistory = (promoterId, month, data) =>
  setDoc(doc(db, COLLECTIONS.INCOME_HISTORY, `${promoterId}_${month}`),
    { promoterId, month, ...data, source: "import", updatedAt: serverTimestamp() }, { merge: true });

export const getIncomeHistoryForPromoter = async (promoterId) => {
  const q = query(collection(db, COLLECTIONS.INCOME_HISTORY), where("promoterId", "==", promoterId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Toàn bộ số liệu tháng cũ đã import (cho báo cáo bán hàng nhiều tháng)
export const getIncomeHistoryAll = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.INCOME_HISTORY));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── PAYROLL: điều chỉnh công + chốt sổ + cấu hình chi phí ──
// Điều chỉnh công (vd phép năm): doc id promoterId_month, extraDays có thể âm
export const setPayrollAdjustment = (promoterId, month, { extraDays, extraBonus, extraOtNonTax, note, promoterName = "" }) => {
  const d = Number(extraDays) || 0;
  const b = Number(extraBonus) || 0;
  const ot = Number(extraOtNonTax) || 0;
  const parts = [];
  if (d !== 0) parts.push(`${d > 0 ? "+" : ""}${d} công`);
  if (b !== 0) parts.push(`${b > 0 ? "+" : ""}${b.toLocaleString("vi-VN")}đ thưởng CT`);
  if (ot !== 0) parts.push(`${ot > 0 ? "+" : ""}${ot.toLocaleString("vi-VN")}đ OT miễn thuế`);
  addAuditLog({ type: "payroll_adjusted", promoterId, promoterName, detail: `Điều chỉnh ${promoterName} tháng ${month}: ${parts.join(", ") || "0"}${note ? ` (${note})` : ""}`, meta: { month } });
  return setDoc(doc(db, COLLECTIONS.PAYROLL_ADJUSTMENTS, `${promoterId}_${month}`),
    { promoterId, month, extraDays: d, extraBonus: b, extraOtNonTax: ot, note: note || "", updatedAt: serverTimestamp() }, { merge: true });
};

export const deletePayrollAdjustment = (promoterId, month) =>
  deleteDoc(doc(db, COLLECTIONS.PAYROLL_ADJUSTMENTS, `${promoterId}_${month}`));

export const getPayrollAdjustments = async (month) => {
  const q = query(collection(db, COLLECTIONS.PAYROLL_ADJUSTMENTS), where("month", "==", month));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Lấy điều chỉnh của 1 promoter/tháng (doc get trực tiếp — rẻ, dùng cho dashboard promoter)
export const getPayrollAdjustment = async (promoterId, month) => {
  const snap = await getDoc(doc(db, COLLECTIONS.PAYROLL_ADJUSTMENTS, `${promoterId}_${month}`));
  return snap.exists() ? snap.data() : null;
};

// Chốt sổ tháng
export const getPayrollClosing = async (month) => {
  const snap = await getDoc(doc(db, COLLECTIONS.PAYROLL_CLOSINGS, month));
  return snap.exists() ? snap.data() : null;
};

export const setPayrollClosing = (month, closed, byName = "Admin") => {
  addAuditLog({ type: closed ? "payroll_closed" : "payroll_reopened", detail: `${byName} ${closed ? "chốt sổ" : "mở lại sổ"} lương tháng ${month}`, meta: { month } });
  return setDoc(doc(db, COLLECTIONS.PAYROLL_CLOSINGS, month),
    { month, closed, closedBy: byName, closedAt: serverTimestamp() }, { merge: true });
};

// Cấu hình chi phí công ty (bảo hiểm %, phí quản lý, VAT)
export const getCompanyCostConfig = async () => {
  const snap = await getDoc(doc(db, COLLECTIONS.COMPANY_COST_CONFIG, "main"));
  return snap.exists() ? snap.data() : null;
};

export const saveCompanyCostConfig = (data) => {
  addAuditLog({ type: "bonus_config_saved", detail: `Cập nhật cấu hình chi phí công ty`, meta: {} });
  return setDoc(doc(db, COLLECTIONS.COMPANY_COST_CONFIG, "main"), { ...data, updatedAt: serverTimestamp() });
};

// ── CẤU HÌNH TẬP TRUNG (phiên bản theo tháng áp dụng) ──────
// Hai collection song song: phần chung (mọi người đọc) và phần chi phí agency (chỉ admin).
// Trang Cấu hình xem chúng như một bộ; các hàm dưới ghép/tách cho đúng chỗ.
export const getPayrollConfigs = async ({ includeCost = false } = {}) => {
  const snap = await getDocs(collection(db, COLLECTIONS.PAYROLL_CONFIG));
  const versions = snap.docs.map(d => ({ effectiveFrom: d.id, ...d.data() }));
  if (includeCost) {
    try {
      const cs = await getDocs(collection(db, COLLECTIONS.PAYROLL_COST_CONFIG));
      const costMap = {};
      cs.docs.forEach(d => { costMap[d.id] = d.data().cost; });
      versions.forEach(v => { if (costMap[v.effectiveFrom]) v.cost = costMap[v.effectiveFrom]; });
    } catch { /* không phải admin → bỏ qua phần chi phí */ }
  }
  return versions.sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
};

export const savePayrollConfig = async (effectiveFrom, { work, bonus, tax, rules, cost }, byName = "Admin") => {
  await setDoc(doc(db, COLLECTIONS.PAYROLL_CONFIG, effectiveFrom),
    { work, bonus, tax, rules, updatedBy: byName, updatedAt: serverTimestamp() });
  await setDoc(doc(db, COLLECTIONS.PAYROLL_COST_CONFIG, effectiveFrom),
    { cost, updatedBy: byName, updatedAt: serverTimestamp() });
  addAuditLog({
    type: "payroll_config_saved",
    detail: `${byName} lưu cấu hình áp dụng từ tháng ${effectiveFrom}`, meta: { month: effectiveFrom },
  });
};

export const deletePayrollConfig = async (effectiveFrom, byName = "Admin") => {
  await deleteDoc(doc(db, COLLECTIONS.PAYROLL_CONFIG, effectiveFrom));
  await deleteDoc(doc(db, COLLECTIONS.PAYROLL_COST_CONFIG, effectiveFrom)).catch(() => {});
  addAuditLog({
    type: "payroll_config_deleted",
    detail: `${byName} xóa phiên bản cấu hình tháng ${effectiveFrom}`, meta: { month: effectiveFrom },
  });
};

// ── ĐĂNG KÝ TĂNG CA ────────────────────────────────────────
// Một đơn / promoter / ngày (doc id cố định để không trùng). Chỉ giờ làm vượt giờ chuẩn
// của ngày có đơn ĐÃ DUYỆT mới được tính OT, tối đa bằng số giờ đã đăng ký.
const OT_KIND_LABEL = { extra: "tăng ca", weekly_off: "làm ngày nghỉ tuần", holiday: "làm ngày lễ" };
export const requestOvertime = async ({ promoterId, promoterName, date, hours, reason, kind = "extra" }) => {
  const id = `${promoterId}_${date}`;
  const existing = await getDoc(doc(db, COLLECTIONS.OT_REQUESTS, id));
  if (existing.exists()) throw new Error("Ngày này đã đăng ký tăng ca rồi");
  await setDoc(doc(db, COLLECTIONS.OT_REQUESTS, id), {
    promoterId, promoterName, date, month: date.slice(0, 7),
    hours: Number(hours) || 0, reason: reason || "", kind,
    status: "pending", requestedAt: serverTimestamp(),
  });
  addAuditLog({
    type: "ot_requested", promoterId, promoterName,
    detail: `${promoterName} đăng ký ${OT_KIND_LABEL[kind] || "tăng ca"} ${hours}h ngày ${date}${reason ? ` — ${reason}` : ""}`,
    meta: { date, month: date.slice(0, 7) },
  });
};

export const approveOvertime = async (id, byName = "Admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.OT_REQUESTS, id));
  const r = snap.exists() ? snap.data() : {};
  await updateDoc(doc(db, COLLECTIONS.OT_REQUESTS, id),
    { status: "approved", approvedBy: byName, approvedAt: serverTimestamp() });
  addAuditLog({
    type: "ot_approved", promoterId: r.promoterId || "", promoterName: r.promoterName || "",
    detail: `${byName} duyệt tăng ca ${r.hours || 0}h ngày ${r.date || ""} của ${r.promoterName || ""}`,
    meta: { date: r.date, month: r.month },
  });
};

// Admin nhập thẳng giờ tăng ca / làm lễ / làm ngày nghỉ tuần (đã duyệt sẵn), ví dụ khi đối chiếu
// với bảng lương agency hoặc promoter báo qua kênh chat. Ghi đè đơn cũ của ngày đó nếu có.
// byAdmin = true → bảng lương dùng đúng số giờ này, không giới hạn theo giờ chấm công.
export const setOvertimeByAdmin = async ({ promoterId, promoterName, date, hours, kind = "extra", note = "", byName = "Admin" }) => {
  const id = `${promoterId}_${date}`;
  await setDoc(doc(db, COLLECTIONS.OT_REQUESTS, id), {
    promoterId, promoterName, date, month: date.slice(0, 7),
    hours: Number(hours) || 0, reason: note || "", kind,
    status: "approved", byAdmin: true,
    approvedBy: byName, approvedAt: serverTimestamp(), requestedAt: serverTimestamp(),
  });
  addAuditLog({
    type: "ot_set_by_admin", promoterId, promoterName,
    detail: `${byName} nhập ${OT_KIND_LABEL[kind] || "tăng ca"} ${hours}h ngày ${date} cho ${promoterName}${note ? ` — ${note}` : ""}`,
    meta: { date, month: date.slice(0, 7) },
  });
};

// Từ chối hoặc promoter tự rút đơn đang chờ
export const deleteOvertime = async (id, byName = "Admin", rejected = true) => {
  const snap = await getDoc(doc(db, COLLECTIONS.OT_REQUESTS, id));
  const r = snap.exists() ? snap.data() : {};
  await deleteDoc(doc(db, COLLECTIONS.OT_REQUESTS, id));
  addAuditLog({
    type: rejected ? "ot_rejected" : "ot_withdrawn",
    promoterId: r.promoterId || "", promoterName: r.promoterName || "",
    detail: rejected
      ? `${byName} từ chối tăng ca ngày ${r.date || ""} của ${r.promoterName || ""}`
      : `${r.promoterName || ""} rút đơn tăng ca ngày ${r.date || ""}`,
    meta: { date: r.date, month: r.month },
  });
};

export const getOvertimeByMonth = async (month, promoterId = null) => {
  const conds = [where("month", "==", month)];
  if (promoterId) conds.push(where("promoterId", "==", promoterId));
  const snap = await getDocs(query(collection(db, COLLECTIONS.OT_REQUESTS), ...conds));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getPendingOvertime = async (promoterIds = null) => {
  const snap = await getDocs(query(collection(db, COLLECTIONS.OT_REQUESTS), where("status", "==", "pending")));
  let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (promoterIds) list = list.filter(r => promoterIds.includes(r.promoterId));
  return list.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
};

// ── THUẾ TNCN & BẢO HIỂM ───────────────────────────────────
// Thiếu doc → trả null, nơi gọi tự dùng DEFAULT_TAX_CONFIG
export const getTaxConfig = async () => {
  const snap = await getDoc(doc(db, COLLECTIONS.TAX_CONFIG, "main"));
  return snap.exists() ? snap.data() : null;
};

export const saveTaxConfig = (data) => {
  addAuditLog({ type: "tax_config_saved", detail: `Cập nhật cấu hình thuế & bảo hiểm`, meta: {} });
  return setDoc(doc(db, COLLECTIONS.TAX_CONFIG, "main"), { ...data, updatedAt: serverTimestamp() });
};

// Điều chỉnh diện đóng BH theo từng người/tháng (đối chiếu hóa đơn agency)
export const getInsuranceAdjustments = async (month) => {
  const q = query(collection(db, COLLECTIONS.INSURANCE_ADJUSTMENTS), where("month", "==", month));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const setInsuranceAdjustment = (promoterId, month, { enabled, baseOverride, note, promoterName = "" }) => {
  const parts = [];
  if (enabled === false) parts.push("không thuộc diện đóng BH");
  if (baseOverride != null) parts.push(`lương đóng BH = ${Number(baseOverride).toLocaleString("vi-VN")}đ`);
  addAuditLog({
    type: "insurance_adjusted", promoterId, promoterName,
    detail: `Điều chỉnh BH ${promoterName} tháng ${month}: ${parts.join(", ") || "về mặc định"}${note ? ` (${note})` : ""}`,
    meta: { month },
  });
  return setDoc(doc(db, COLLECTIONS.INSURANCE_ADJUSTMENTS, `${promoterId}_${month}`),
    { promoterId, month, enabled: enabled !== false,
      baseOverride: baseOverride == null ? null : Number(baseOverride),
      note: note || "", updatedAt: serverTimestamp() }, { merge: true });
};

// ── TÀI LIỆU TRAINING (link) ───────────────────────────────
export const addTrainingDoc = (data) => {
  addAuditLog({ type: "training_added", detail: `Thêm tài liệu training: "${data?.title || ""}"`, meta: {} });
  return addDoc(collection(db, COLLECTIONS.TRAINING_DOCS), { ...data, createdAt: serverTimestamp() });
};

export const getTrainingDocs = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.TRAINING_DOCS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
};

export const updateTrainingDoc = (id, data) => {
  addAuditLog({ type: "training_updated", detail: `Sửa tài liệu training: "${data?.title || ""}"`, meta: { id } });
  return updateDoc(doc(db, COLLECTIONS.TRAINING_DOCS, id), data);
};
export const deleteTrainingDoc = (id) => {
  addAuditLog({ type: "training_deleted", detail: `Xóa tài liệu training`, meta: { id } });
  return deleteDoc(doc(db, COLLECTIONS.TRAINING_DOCS, id));
};

// ── NGHỈ CÓ LƯƠNG (admin tạo) ──────────────────────────────
// Admin xác nhận đủ công cho ngày promoter không check-in được (app lỗi, báo qua chat).
// Ghi dayType riêng để phân biệt với ngày có ảnh chấm công thật.
export const setManualWorkDay = async ({ promoterId, date, hours = 8 }) => {
  const existing = await getAttendanceByDate(promoterId, date);
  if (existing) throw new Error("Ngày này đã có chấm công");
  const holiday = await isHolidayDate(date);
  const h = Number(hours) || 8;
  return addDoc(collection(db, COLLECTIONS.ATTENDANCE), {
    promoterId, date,
    checkinTime: "—", checkoutTime: "—",
    checkinPhoto: null, checkoutPhoto: null,
    totalHours: h,
    isHoliday: !!holiday, holidayMultiplier: holiday?.multiplier || 1,
    dayType: DAY_TYPES.MANUAL_WORK, scheduledHours: h, dayCredit: h / 8,
  });
};

// ── ĐĂNG KÝ NGHỈ (promoter tự đăng ký, quản lý duyệt) ──────
// Tạo bản ghi chấm công với leaveStatus "pending" — chưa tính công cho tới khi được duyệt.
export const requestLeave = async ({ promoterId, promoterName, date, dayType, reason }) => {
  const existing = await getAttendanceByDate(promoterId, date);
  if (existing) throw new Error("Ngày này đã có chấm công hoặc đã đăng ký nghỉ");
  const ref = await addDoc(collection(db, COLLECTIONS.ATTENDANCE), {
    promoterId, date, dayType,
    leaveStatus: "pending", leaveReason: reason || "",
    checkinTime: null, checkoutTime: null, checkinPhoto: null, checkoutPhoto: null,
    totalHours: 0, isHoliday: false, holidayMultiplier: 1, dayCredit: 0,
    requestedAt: serverTimestamp(),
  });
  addAuditLog({
    type: "leave_requested", promoterId, promoterName,
    detail: `${promoterName} xin nghỉ ngày ${date}${reason ? ` — ${reason}` : ""}`,
    meta: { date, dayType },
  });
  return ref;
};

// Duyệt: phép năm được tính 1 công, nghỉ không phép 0 công
export const approveLeave = async (id, byName = "Admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.ATTENDANCE, id));
  const r = snap.exists() ? snap.data() : {};
  const credit = r.dayType === DAY_TYPES.ANNUAL_LEAVE ? 1 : 0;
  await updateDoc(doc(db, COLLECTIONS.ATTENDANCE, id), {
    leaveStatus: "approved", dayCredit: credit,
    checkinTime: credit ? "—" : null, checkoutTime: credit ? "—" : null,
    approvedBy: byName, approvedAt: serverTimestamp(),
  });
  addAuditLog({
    type: "leave_approved", promoterId: r.promoterId || "",
    detail: `${byName} duyệt đơn nghỉ ngày ${r.date || ""}`, meta: { date: r.date },
  });
};

export const rejectLeave = async (id, byName = "Admin") => {
  const snap = await getDoc(doc(db, COLLECTIONS.ATTENDANCE, id));
  const r = snap.exists() ? snap.data() : {};
  await deleteDoc(doc(db, COLLECTIONS.ATTENDANCE, id));
  addAuditLog({
    type: "leave_rejected", promoterId: r.promoterId || "",
    detail: `${byName} từ chối đơn nghỉ ngày ${r.date || ""}`, meta: { date: r.date },
  });
};

// Đơn nghỉ đang chờ duyệt (mọi tháng); truyền promoterIds để lọc cho supervisor
export const getPendingLeaves = async (promoterIds = null) => {
  const q = query(collection(db, COLLECTIONS.ATTENDANCE), where("leaveStatus", "==", "pending"));
  const snap = await getDocs(q);
  let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (promoterIds) list = list.filter(r => promoterIds.includes(r.promoterId));
  return list.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
};

// Xóa bản ghi chấm công (chỉ dùng để gỡ đánh dấu ngày nghỉ do admin tạo)
export const deleteAttendance = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.ATTENDANCE, id));
  const r = snap.exists() ? snap.data() : {};
  await deleteDoc(doc(db, COLLECTIONS.ATTENDANCE, id));
  addAuditLog({
    type: "attendance_day_cleared", promoterId: r.promoterId || "",
    detail: `Gỡ đánh dấu ngày nghỉ ${r.date || ""}`, meta: { date: r.date },
  });
};

export const setPaidLeave = async ({ promoterId, date, dayType = DAY_TYPES.PAID_LEAVE }) => {
  const existing = await getAttendanceByDate(promoterId, date);
  if (existing) throw new Error("Ngày này đã có chấm công");
  return addDoc(collection(db, COLLECTIONS.ATTENDANCE), {
    promoterId, date,
    checkinTime: "—", checkoutTime: "—",
    checkinPhoto: null, checkoutPhoto: null,
    totalHours: 0, isHoliday: false, holidayMultiplier: 1,
    dayType, dayCredit: 1,
  });
};

// ── NOTIFICATIONS ──────────────────────────────────────────
// (Log notification_sent được ghi ở trang gửi để kèm thông tin người nhận)
export const sendNotification = (data) =>
  addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), { ...data, createdAt: serverTimestamp() });

export const getNotifications = async (uid = null, role = null) => {
  const snap = await getDocs(query(collection(db, COLLECTIONS.NOTIFICATIONS), orderBy("createdAt", "desc")));
  const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (!uid) return all;
  return all.filter(n =>
    n.sentTo === "all" ||
    (n.sentTo === "supervisors" && role === "supervisor") ||
    (Array.isArray(n.sentTo) && n.sentTo.includes(uid))
  );
};

// ── SUPERVISOR ─────────────────────────────────────────────

// Tạo tài khoản supervisor (chỉ admin)
export const createSupervisor = async ({ email, password, name }) => {
  const user = await createAccount(email, password, { role: "supervisor", name });
  addAuditLog({ type: "supervisor_created", detail: `Tạo supervisor "${name}"`, meta: {} });
  return user;
};

// Lấy tất cả supervisor
export const getAllSupervisors = async () => {
  const q = query(collection(db, COLLECTIONS.USERS), where("role", "==", "supervisor"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
};

// Lấy danh sách promoterId được assign cho supervisor
export const getSupervisorAssignment = async (supervisorId) => {
  const snap = await getDoc(doc(db, COLLECTIONS.SUPERVISOR_ASSIGNMENTS, supervisorId));
  return snap.exists() ? snap.data().promoterIds || [] : [];
};

// Lưu danh sách promoterId cho supervisor
export const setSupervisorAssignment = (supervisorId, promoterIds) => {
  addAuditLog({ type: "assignment_changed", detail: `Cập nhật phân công supervisor (${promoterIds.length} promoter)`, meta: { supervisorId } });
  return setDoc(doc(db, COLLECTIONS.SUPERVISOR_ASSIGNMENTS, supervisorId), { promoterIds });
};

// ── Đọc dữ liệu theo nhóm (supervisor) ──
// Rules chỉ cho supervisor đọc users/sales/targets/incomeHistory của promoter mình quản lý,
// nên mọi query của supervisor phải lọc promoterId in [nhóm] (tối đa 30 id mỗi query).
const queryByPromoters = async (col, promoterIds, ...filters) => {
  const ids = [...new Set(promoterIds || [])];
  const chunks = [];
  for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
  const snaps = await Promise.all(chunks.map(c =>
    getDocs(query(collection(db, col), where("promoterId", "in", c), ...filters))
  ));
  return snaps.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
};

// Lấy danh sách promoter đầy đủ của supervisor (đọc từng doc — không liệt kê toàn bộ users)
export const getAssignedPromoters = async (supervisorId, includeArchived = false) => {
  const ids = await getSupervisorAssignment(supervisorId);
  const snaps = await Promise.all(ids.map(id => getDoc(doc(db, COLLECTIONS.USERS, id)).catch(() => null)));
  return snaps
    .filter(snap => snap?.exists() && snap.data().role === "promoter")
    .map(snap => ({ uid: snap.id, ...snap.data() }))
    .filter(p => includeArchived || p.active !== false);
};

// Pending sales chỉ trong nhóm supervisor (mới nhất trước)
export const getPendingSalesByPromoters = async (promoterIds) => {
  const list = await queryByPromoters(COLLECTIONS.SALES, promoterIds, where("status", "==", "pending"));
  const ts = (s) => s.submittedAt?.toMillis?.() ?? 0;
  return list.sort((a, b) => ts(b) - ts(a));
};

// Sales đã duyệt theo tháng trong nhóm supervisor
export const getSalesByMonthForPromoters = (month, promoterIds) =>
  queryByPromoters(COLLECTIONS.SALES, promoterIds, where("month", "==", month), where("status", "==", "approved"));

// Target theo tháng trong nhóm supervisor
export const getTargetsForPromoters = async (month, promoterIds, approvedOnly = true) => {
  const list = await queryByPromoters(COLLECTIONS.TARGETS, promoterIds, where("month", "==", month));
  return approvedOnly ? list.filter(t => t.status !== "pending") : list;
};

// Số liệu tháng cũ đã import trong nhóm supervisor
export const getIncomeHistoryForPromoters = (promoterIds) =>
  queryByPromoters(COLLECTIONS.INCOME_HISTORY, promoterIds);

// Attendance appeals pending trong nhóm supervisor
export const getPendingAppealsByPromoters = async (promoterIds) => {
  if (promoterIds.length === 0) return [];
  const all = await getPendingAppeals();
  return all.filter(a => promoterIds.includes(a.promoterId));
};

// Attendance của nhiều promoter trong 1 tháng (dùng cho supervisor overview)
export const getAllAttendanceByMonthForPromoters = async (monthPrefix, promoterIds) => {
  if (promoterIds.length === 0) return [];
  const all = await getAllAttendanceByMonth(monthPrefix);
  return all.filter(r => promoterIds.includes(r.promoterId));
};

// ── PHOTO CLEANUP ───────────────────────────────────────────
/**
 * Dọn dẹp ảnh attendance cũ hơn `monthsOld` tháng.
 * - Xoá file trên Firebase Storage (checkinPhoto + checkoutPhoto)
 * - Xoá trường URL trong Firestore (set null) để tránh broken link
 * - Trả về số record đã xử lý
 */
export const cleanupOldAttendancePhotos = async (monthsOld = 3, onProgress = null) => {
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - monthsOld);
  const cutoffStr = cutoffDate.toISOString().slice(0, 10); // "yyyy-MM-dd"

  const snap = await getDocs(collection(db, COLLECTIONS.ATTENDANCE));
  const records = snap.docs
    .map(d => ({ docRef: d.ref, ...d.data() }))
    .filter(r => r.date && r.date < cutoffStr && (r.checkinPhoto || r.checkoutPhoto));

  let done = 0;
  for (const r of records) {
    const updates = {};
    // Xoá ảnh check-in
    if (r.checkinPhoto) {
      try {
        // Firebase Storage URL → extract path
        const url = new URL(r.checkinPhoto);
        const pathEncoded = url.pathname.split("/o/")[1]?.split("?")[0];
        if (pathEncoded) await deleteObject(ref(storage, decodeURIComponent(pathEncoded)));
      } catch (_) { /* file đã bị xoá rồi, bỏ qua */ }
      updates.checkinPhoto = null;
    }
    // Xoá ảnh check-out
    if (r.checkoutPhoto) {
      try {
        const url = new URL(r.checkoutPhoto);
        const pathEncoded = url.pathname.split("/o/")[1]?.split("?")[0];
        if (pathEncoded) await deleteObject(ref(storage, decodeURIComponent(pathEncoded)));
      } catch (_) { /* đã xoá */ }
      updates.checkoutPhoto = null;
    }
    await updateDoc(r.docRef, updates);
    done++;
    if (onProgress) onProgress(done, records.length);
  }
  return done;
};

// ── MASTER THÔNG SỐ MSI (file "specs … sorted.xlsx", sheet NB) ──
export const getSkuMaster = async () => {
  const snap = await getDoc(doc(db, COLLECTIONS.SKU_MASTER, "current"));
  return snap.exists() ? snap.data() : null;
};

export const getSkuSpecs = async (partNo) => {
  if (!partNo) return null;
  const snap = await getDoc(doc(db, COLLECTIONS.SKU_SPECS, specDocId(partNo)));
  return snap.exists() ? snap.data() : null;
};

// Ghi theo lô (Firestore giới hạn 500 thao tác / lô)
const runBatched = async (ops) => {
  for (let i = 0; i < ops.length; i += 400) {
    const b = writeBatch(db);
    ops.slice(i, i + 400).forEach(fn => fn(b));
    await b.commit();
  }
};

// Trường gọn trên bảng SKU, lấy từ thông số master theo "Cấu hình trường"
export const skuFromMaster = (product, fields) => {
  const v = product.values;
  const mapped = mapFields(v, fields);
  mapped.partNo = product.partNo;
  if (!mapped.model) mapped.model = v["MKT Spec"] || v["MKT Name"] || product.partNo;
  return mapped;
};
const autoHighend = (product, mapped) =>
  HIGHEND_GPU.test([product.values["GPU1 MKT Name"], product.values["GPU1 Number"], mapped.gpu].filter(Boolean).join(" "));

/**
 * Áp dụng file master: thêm SKU mới, cập nhật SKU đang có (giữ Highend + ghi chú admin đã chỉnh),
 * lưu toàn bộ thông số vào skuSpecs, và đánh dấu "ngừng cung cấp" SKU không còn trong master.
 */
export const applySkuMaster = async ({ fileName, master, products, fields, existingSkus, byName = "Admin" }) => {
  const now = serverTimestamp();
  const inMaster = new Set(products.map(p => p.partNo));
  const byPart = {};
  existingSkus.forEach(s => { if (s.partNo) byPart[s.partNo] = s; });
  const ops = [];
  let added = 0, updated = 0, discontinued = 0;

  for (const p of products) {
    const mapped = skuFromMaster(p, fields);
    const data = { ...mapped, status: "active", discontinuedAt: null, masterFile: fileName, masterUploadedAt: now };
    const ex = byPart[p.partNo];
    if (ex) {
      ops.push(b => b.update(doc(db, COLLECTIONS.SKUS, ex.id), { ...data, updatedAt: now }));
      updated++;
    } else {
      const ref = doc(collection(db, COLLECTIONS.SKUS));
      ops.push(b => b.set(ref, { ...data, isHighend: autoHighend(p, mapped), notes: "", createdAt: now }));
      added++;
    }
    ops.push(b => b.set(doc(db, COLLECTIONS.SKU_SPECS, specDocId(p.partNo)), { partNo: p.partNo, values: p.values, uploadedAt: now }));
  }
  for (const s of existingSkus) {
    if (s.partNo && !inMaster.has(s.partNo) && s.status !== "discontinued") {
      ops.push(b => b.update(doc(db, COLLECTIONS.SKUS, s.id), { status: "discontinued", discontinuedAt: now }));
      discontinued++;
    }
  }
  ops.push(b => b.set(doc(db, COLLECTIONS.SKU_MASTER, "current"), {
    fileName, sheetName: master.sheetName, columns: master.columns, count: products.length,
    uploadedAt: now, uploadedBy: byName,
  }));
  await runBatched(ops);
  addAuditLog({
    type: "sku_master_uploaded",
    detail: `${byName} up master thông số "${fileName}": ${products.length} SKU (+${added} mới, ${updated} cập nhật, ${discontinued} ngừng cung cấp)`,
    meta: { fileName },
  });
  return { added, updated, discontinued };
};

// Sau khi đổi "Cấu hình trường": dựng lại các cột gọn từ thông số master đã lưu, không cần up lại file
export const remapSkusFromSpecs = async (fields) => {
  const [skuSnap, specSnap] = await Promise.all([
    getDocs(collection(db, COLLECTIONS.SKUS)), getDocs(collection(db, COLLECTIONS.SKU_SPECS)),
  ]);
  const specs = {};
  specSnap.docs.forEach(d => { const x = d.data(); if (x.partNo) specs[x.partNo] = x; });
  const ops = [];
  skuSnap.docs.forEach(d => {
    const s = d.data();
    const sp = s.partNo && specs[s.partNo];
    if (!sp) return;
    const mapped = skuFromMaster({ partNo: sp.partNo, values: sp.values || {} }, fields);
    ops.push(b => b.update(d.ref, mapped));
  });
  await runBatched(ops);
  return ops.length;
};

// ── SKU FIELD CONFIG ─────────────────────────────────────────
export const getSKUFieldConfig = async () => {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.SKU_CONFIG, "main"));
    return snap.exists() ? snap.data().fields : null;
  } catch { return null; }
};

export const saveSKUFieldConfig = async (fields) => {
  await setDoc(doc(db, COLLECTIONS.SKU_CONFIG, "main"), {
    fields,
    updatedAt: serverTimestamp(),
  });
};

// ── BANNERS ─────────────────────────────────────────────────
export const getBanners = async () => {
  const snap = await getDocs(collection(db, "banners"));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b => b.active !== false)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
};

export const saveBanner = (id, data) => {
  addAuditLog({ type: "banner_saved", detail: `Cập nhật banner ${id}`, meta: { id } });
  return setDoc(doc(db, "banners", id), { ...data, active: true }, { merge: true });
};

export const deleteBanner = (id) => {
  addAuditLog({ type: "banner_deleted", detail: `Xóa banner ${id}`, meta: { id } });
  return setDoc(doc(db, "banners", id), { active: false }, { merge: true });
};
